import { useEffect, useRef, useState } from "react";
import { useToast } from "@pps/ui/hooks/use-toast";

/**
 * Detects when a new service worker is waiting and prompts the user to update.
 * The `registerSW` handle is stored on `window.__pwa_update_sw__` by main.tsx.
 * This hook is a no-op when running inside Capacitor native (no SW registered).
 */
export function usePwaUpdate() {
  const { toast } = useToast();
  const updateSwRef = useRef<((reloadPage?: boolean) => Promise<void>) | null>(null);
  const [needsUpdate, setNeedsUpdate] = useState(false);

  useEffect(() => {
    const handle = (
      window as Window & {
        __pwa_update_sw__?: (reloadPage?: boolean) => Promise<void>;
      }
    ).__pwa_update_sw__;

    if (!handle) return; // Capacitor native or SW not registered

    // Re-wrap so we can intercept the "needs update" signal.
    // vite-plugin-pwa's registerSW with registerType:"prompt" calls onNeedRefresh
    // via the returned function's onNeedRefresh property; we attach it here.
    updateSwRef.current = handle;

    // Poll for the onNeedRefresh callback support (vite-plugin-pwa attaches it).
    const registered = handle as typeof handle & {
      onNeedRefresh?: () => void;
      onOfflineReady?: () => void;
    };

    registered.onNeedRefresh = () => {
      setNeedsUpdate(true);
    };

    registered.onOfflineReady = () => {
      toast({
        title: "Modo offline ativo",
        description: "O app está pronto para uso sem internet.",
        duration: 4000,
      });
    };
  }, [toast]);

  // Show a persistent toast whenever a new version is ready
  useEffect(() => {
    if (!needsUpdate) return;

    toast({
      title: "Nova versão disponível",
      description: "Clique em Atualizar para carregar a versão mais recente.",
      duration: 0, // persistent until dismissed
      action: (
        <button
          className="rounded bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          onClick={() => updateSwRef.current?.(true)}
        >
          Atualizar
        </button>
      ),
    });
  }, [needsUpdate, toast]);
}
